import axios from "axios";
import { Loader2, Plus } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import EditorTextArea from "../../../../../components/EditorTextArea";
import FileUploadVideo from "../../../../../components/FileUploadVideo";
import { Button } from "../../../../../components/ui/button";
import { Card, CardContent } from "../../../../../components/ui/card";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../../../../../components/ui/drawer";
import { Input } from "../../../../../components/ui/input";
import { useLanguage } from "../../../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../../../lib/api";
import {
  SubLessonData
} from "../../../../../lib/languages";
import MainButton from "../../../../../components/MainButton";
import { useLocation, useParams } from "react-router-dom";

const DrawerAddContent = ({ fetchContents }) => {
  const { id } = useParams();
  const { language } = useLanguage();
  const t = SubLessonData[language] || SubLessonData["en"];
  const location = useLocation();
  const courseData = location.state?.courseData;

  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [video, setVideo] = useState(null);
  const [form, setForm] = useState({
    title_en: "",
    title_ar: "",
    description_en: "",
    description_ar: "",
    live_link: "",
  });

  const handleAddContent = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      if (form.title_ar || form.title_en) {
        formData.append(
          "title",
          JSON.stringify({ ar: form.title_ar, en: form.title_en })
        );
      }
      if (form.description_ar || form.description_en) {
        formData.append(
          "description",
          JSON.stringify({ ar: form.description_ar, en: form.description_en })
        );
      }
      formData.append("video_path", video || "");
      formData.append("live_link", form.live_link || "");

      await axios.post(`${BaseUrlApi}/lesson/${id}/contents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast(t.contentCreatedToastTitle, {
        description: t.contentCreatedToastDescription,
      });
      await fetchContents();
      setOpen(false);
      clearAll();
    } catch (error) {
      setErrors(ErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const clearAll = () => {
    setErrors({});
    setForm({
      title_en: "",
      title_ar: "",
      description_en: "",
      description_ar: "",
      live_link: "",
    });
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <div className="text-end">
          <MainButton Icon={Plus} title={t.addContent} />
        </div>
      </DrawerTrigger>
      <DrawerContent dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="h-[calc(100vh-2rem)] max-w-7xl w-full mx-auto overflow-auto hide-scrollbar space-y-4 p-4">
          {/* Header */}
          <DrawerHeader className="flex items-center justify-between flex-wrap gap-4 p-0 pb-2">
            <div className="w-full sm:w-fit">
              <DrawerTitle className="font-bold text-2xl">
                {t.addContent}
              </DrawerTitle>
              <DrawerDescription className="font-medium text-sm">
                {t.addContentDescription}
              </DrawerDescription>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-fit">
              <Button
                className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl z-10"
                onClick={handleAddContent}
                disabled={loading}
              >
                {loading && (
                  <div className="animate-spin">
                    <Loader2 />
                  </div>
                )}
                {t.addContentButton}
              </Button>
              <DrawerClose asChild>
                <Button
                  variant="outline"
                  className="w-full sm:w-fit h-10 border-2 border-blue-600 text-blue-600 hover:text-blue-500 rounded-xl"
                >
                  {t.cancel}
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>
          {/* Uploads */}
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4 relative">
            <Card>
              <CardContent className="p-4 space-y-4 flex flex-col h-full">
                <div className="flex items-center gap-2 justify-between">
                  <p className="font-bold text-xl flex items-center gap-2">
                    {!!courseData?.type ? t.live : t.video}
                    <div
                      className={`relative size-4 ${
                        !!courseData?.type ? "bg-red-500" : "bg-green-500"
                      } rounded-full`}
                    >
                      {!!courseData?.type && (
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
                      )}
                    </div>
                  </p>
                </div>
                {form.type ? (
                  <>
                    <div className="flex flex-col gap-2 w-full">
                      <label htmlFor="live_link" className="text-sm">
                        {"Live Link"}
                      </label>
                      <Input
                        id="live_link"
                        name="live_link"
                        type="text"
                        placeholder={"Enter Link of live"}
                        onChange={(e) =>
                          setForm({ ...form, live_link: e.target.value })
                        }
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <FileUploadVideo
                      onUploadComplete={(url) => setVideo(url)}
                      name={"contents"}
                    />
                    {errors?.video && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.video[0]}
                      </p>
                    )}
                    <div className="font-normal text-sm text-muted-foreground text-center">
                      {t.onlyVideosMessage}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
          {/* General Information */}
          <Card>
            <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
              <p className="font-bold text-xl border-b pb-2">
                {t.generalInformation}
              </p>
              <div className="space-y-4 flex-1 flex flex-col">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_en" className="text-sm">
                      {t.titleEn}
                    </label>
                    <Input
                      id="title_en"
                      name="title_en"
                      type="text"
                      placeholder={t.enterTitleEn}
                      onChange={(e) =>
                        setForm({ ...form, title_en: e.target.value })
                      }
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_ar" className="text-sm">
                      {t.titleAr}
                    </label>
                    <Input
                      id="title_ar"
                      name="title_ar"
                      type="text"
                      placeholder={t.enterTitleAr}
                      onChange={(e) =>
                        setForm({ ...form, title_ar: e.target.value })
                      }
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                </div>
                {/* Description */}
                <div className="flex flex-col gap-2 flex-1">
                  <label htmlFor="description" className="text-sm">
                    {t.descriptionLabel}
                  </label>
                  <EditorTextArea
                    form={form}
                    en="description_en"
                    ar="description_ar"
                    setForm={setForm}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default DrawerAddContent;
